import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from '../data/queryKeys';
import type { CreateDealResult, DealSubmitPayload } from './types';

export type UseCreateDealVariables = {
	companyId: string;
	payload: DealSubmitPayload;
};

/**
 * POSTs the Create Deal form for a lead. Mirrors the legacy
 * `CustomFormViewModel.save` but as a clean react-query mutation.
 *
 * On success the lead query is invalidated so the next render reflects the
 * new deal state; the pacing query is also invalidated since a new deal
 * bumps the daily demos count.
 */
export function useCreateDeal() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<CreateDealResult, Error, UseCreateDealVariables>({
		mutationFn: async ({ companyId, payload }) => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<CreateDealResult>(
				ImpersonationBroker.composeApiUrl({ urlPath: `lead/${companyId}/CreateDeal` }),
				'POST',
				{
					...payload.values,
					customProperties: payload.customProperties,
				}
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return result.value ?? {};
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.lead(variables.companyId) });
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.pacing() });
		},
	});
}
